/**
 * useVoiceoverGeneration Hook
 *
 * Manages TTS voiceover generation for reel scenes.
 * Supports single scene and bulk generation.
 *
 * React 19 patterns:
 * - useReducer for complex state
 * - useCallback for event handlers
 * - useMemo for derived values
 */

import { useCallback, useMemo, useReducer } from 'react';
import { useAction, useQuery } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import {
  ISceneVoiceover,
  IVoiceoverGenerationResult,
  IBulkVoiceoverResult,
  IVoiceSettings,
  VoiceoverGenerationStep,
} from '@/@types/voices';

// ============================================
// STATE TYPES
// ============================================

interface VoiceoverGenerationState {
  // Overall step
  step: VoiceoverGenerationStep;

  // Single scene generation
  isGeneratingSingle: boolean;
  generatingSceneId: Id<'reelScenes'> | null;

  // Bulk generation
  isGeneratingBulk: boolean;
  bulkProgress: {
    total: number;
    completed: number;
    failed: number;
  };

  // Error state
  error: string | null;
  errorCode: string | null;

  // Set of scene IDs currently generating
  generatingSceneIds: Id<'reelScenes'>[];

  // Last bulk result
  lastBulkResult: IBulkVoiceoverResult | null;
}

type VoiceoverGenerationAction =
  | { type: 'START_SINGLE_GENERATION'; payload: { sceneId: Id<'reelScenes'> } }
  | { type: 'SINGLE_GENERATION_SUCCESS'; payload: { sceneId: Id<'reelScenes'> } }
  | {
      type: 'SINGLE_GENERATION_ERROR';
      payload: { sceneId: Id<'reelScenes'>; error: string; errorCode?: string };
    }
  | { type: 'START_BULK_GENERATION'; payload: { total: number } }
  | { type: 'BULK_PROGRESS'; payload: { completed: number; failed: number } }
  | { type: 'BULK_GENERATION_COMPLETE'; payload: { result: IBulkVoiceoverResult } }
  | { type: 'BULK_GENERATION_ERROR'; payload: { error: string } }
  | { type: 'ADD_GENERATING_SCENE'; payload: { sceneId: Id<'reelScenes'> } }
  | { type: 'REMOVE_GENERATING_SCENE'; payload: { sceneId: Id<'reelScenes'> } }
  | { type: 'RESET' };

// ============================================
// REDUCER
// ============================================

const initialState: VoiceoverGenerationState = {
  step: 'idle',
  isGeneratingSingle: false,
  generatingSceneId: null,
  isGeneratingBulk: false,
  bulkProgress: {
    total: 0,
    completed: 0,
    failed: 0,
  },
  error: null,
  errorCode: null,
  generatingSceneIds: [],
  lastBulkResult: null,
};

function voiceoverGenerationReducer(
  state: VoiceoverGenerationState,
  action: VoiceoverGenerationAction
): VoiceoverGenerationState {
  switch (action.type) {
    case 'START_SINGLE_GENERATION':
      return {
        ...state,
        step: 'generating',
        isGeneratingSingle: true,
        generatingSceneId: action.payload.sceneId,
        error: null,
        errorCode: null,
        generatingSceneIds: [...state.generatingSceneIds, action.payload.sceneId],
      };

    case 'SINGLE_GENERATION_SUCCESS':
      return {
        ...state,
        step: 'completed',
        isGeneratingSingle: false,
        generatingSceneId: null,
        generatingSceneIds: state.generatingSceneIds.filter(id => id !== action.payload.sceneId),
      };

    case 'SINGLE_GENERATION_ERROR':
      return {
        ...state,
        step: 'error',
        isGeneratingSingle: false,
        generatingSceneId: null,
        error: action.payload.error,
        errorCode: action.payload.errorCode || null,
        generatingSceneIds: state.generatingSceneIds.filter(id => id !== action.payload.sceneId),
      };

    case 'START_BULK_GENERATION':
      return {
        ...state,
        step: 'generating',
        isGeneratingBulk: true,
        bulkProgress: {
          total: action.payload.total,
          completed: 0,
          failed: 0,
        },
        error: null,
        errorCode: null,
        lastBulkResult: null,
      };

    case 'BULK_PROGRESS':
      return {
        ...state,
        bulkProgress: {
          ...state.bulkProgress,
          completed: action.payload.completed,
          failed: action.payload.failed,
        },
      };

    case 'BULK_GENERATION_COMPLETE':
      return {
        ...state,
        step: 'completed',
        isGeneratingBulk: false,
        lastBulkResult: action.payload.result,
        generatingSceneIds: [],
      };

    case 'BULK_GENERATION_ERROR':
      return {
        ...state,
        step: 'error',
        isGeneratingBulk: false,
        error: action.payload.error,
        generatingSceneIds: [],
      };

    case 'ADD_GENERATING_SCENE':
      if (state.generatingSceneIds.includes(action.payload.sceneId)) {
        return state;
      }
      return {
        ...state,
        generatingSceneIds: [...state.generatingSceneIds, action.payload.sceneId],
      };

    case 'REMOVE_GENERATING_SCENE':
      return {
        ...state,
        generatingSceneIds: state.generatingSceneIds.filter(id => id !== action.payload.sceneId),
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

// ============================================
// HOOK
// ============================================

export function useVoiceoverGeneration(
  options: {
    reelId?: Id<'reels'>;
    onSingleSuccess?: (sceneId: Id<'reelScenes'>, voiceoverId: Id<'sceneVoiceovers'>) => void;
    onSingleError?: (sceneId: Id<'reelScenes'>, error: string) => void;
    onBulkComplete?: (result: IBulkVoiceoverResult) => void;
    onBulkError?: (error: string) => void;
  } = {}
) {
  const { reelId, onSingleSuccess, onSingleError, onBulkComplete, onBulkError } = options;

  // ============================================
  // STATE
  // ============================================
  const [state, dispatch] = useReducer(voiceoverGenerationReducer, initialState);

  // ============================================
  // CONVEX QUERIES
  // ============================================

  // Fetch voiceovers for the reel (if reelId is provided)
  const voiceoversForReel = useQuery(
    api.sceneVoiceovers.getVoiceoversForReel,
    reelId ? { reelId } : 'skip'
  );

  // ============================================
  // CONVEX ACTIONS
  // ============================================

  const generateVoiceoverAction = useAction(api.ai.textToSpeech.generateVoiceover);
  const generateBulkVoiceoversAction = useAction(api.ai.textToSpeech.generateBulkVoiceovers);
  const regenerateVoiceoverAction = useAction(api.ai.textToSpeech.regenerateVoiceover);

  // ============================================
  // COMPUTED VALUES
  // ============================================

  const isLoading = useMemo(
    () => reelId !== undefined && voiceoversForReel === undefined,
    [reelId, voiceoversForReel]
  );

  const isGenerating = useMemo(
    () => state.isGeneratingSingle || state.isGeneratingBulk,
    [state.isGeneratingSingle, state.isGeneratingBulk]
  );

  const voiceoversMap = useMemo(() => {
    const map = new Map<string, ISceneVoiceover>();
    if (!voiceoversForReel) return map;

    for (const voiceover of voiceoversForReel) {
      // Use the latest voiceover for each scene
      const existing = map.get(voiceover.sceneId);
      if (!existing || voiceover.generatedAt > existing.generatedAt) {
        map.set(voiceover.sceneId, voiceover as ISceneVoiceover);
      }
    }

    return map;
  }, [voiceoversForReel]);

  const completedVoiceovers = useMemo(() => {
    // Get unique completed voiceovers from the deduplicated map
    // (voiceoversMap keeps only the latest voiceover per scene)
    const completed: ISceneVoiceover[] = [];
    voiceoversMap.forEach(voiceover => {
      if (voiceover.status === 'completed') {
        completed.push(voiceover);
      }
    });
    return completed;
  }, [voiceoversMap]);

  const bulkProgressPercent = useMemo(() => {
    if (state.bulkProgress.total === 0) return 0;
    return Math.round(
      ((state.bulkProgress.completed + state.bulkProgress.failed) / state.bulkProgress.total) * 100
    );
  }, [state.bulkProgress]);

  // ============================================
  // EVENT HANDLERS
  // ============================================

  /**
   * Generate voiceover for a single scene
   */
  const handleGenerateSingle = useCallback(
    async (
      sceneId: Id<'reelScenes'>,
      voiceId: Id<'clonedVoices'>,
      text: string,
      voiceSettings?: IVoiceSettings
    ): Promise<IVoiceoverGenerationResult> => {
      dispatch({ type: 'START_SINGLE_GENERATION', payload: { sceneId } });

      try {
        const result = await generateVoiceoverAction({
          sceneId,
          voiceId,
          text,
          voiceSettings,
        });

        if (!result.success) {
          dispatch({
            type: 'SINGLE_GENERATION_ERROR',
            payload: {
              sceneId,
              error: result.error || 'Generation failed',
              errorCode: result.errorCode,
            },
          });
          toast.error('Voiceover generation failed', {
            description: result.error,
          });
          onSingleError?.(sceneId, result.error || 'Generation failed');
          return result;
        }

        dispatch({ type: 'SINGLE_GENERATION_SUCCESS', payload: { sceneId } });
        toast.success('Voiceover generated successfully');

        if (result.voiceoverId) {
          onSingleSuccess?.(sceneId, result.voiceoverId);
        }

        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        dispatch({
          type: 'SINGLE_GENERATION_ERROR',
          payload: { sceneId, error: errorMessage, errorCode: 'INTERNAL_ERROR' },
        });
        toast.error('Voiceover generation failed', {
          description: errorMessage,
        });
        onSingleError?.(sceneId, errorMessage);
        return {
          success: false,
          error: errorMessage,
          errorCode: 'INTERNAL_ERROR',
        };
      }
    },
    [generateVoiceoverAction, onSingleSuccess, onSingleError]
  );

  /**
   * Regenerate voiceover for a scene (deletes existing and creates new)
   */
  const handleRegenerate = useCallback(
    async (
      sceneId: Id<'reelScenes'>,
      voiceId?: Id<'clonedVoices'>
    ): Promise<IVoiceoverGenerationResult> => {
      dispatch({ type: 'START_SINGLE_GENERATION', payload: { sceneId } });

      try {
        const result = await regenerateVoiceoverAction({
          sceneId,
          voiceId,
        });

        if (!result.success) {
          dispatch({
            type: 'SINGLE_GENERATION_ERROR',
            payload: {
              sceneId,
              error: result.error || 'Regeneration failed',
              errorCode: result.errorCode,
            },
          });
          toast.error('Voiceover regeneration failed', {
            description: result.error,
          });
          onSingleError?.(sceneId, result.error || 'Regeneration failed');
          return result;
        }

        dispatch({ type: 'SINGLE_GENERATION_SUCCESS', payload: { sceneId } });
        toast.success('Voiceover regenerated successfully');

        if (result.voiceoverId) {
          onSingleSuccess?.(sceneId, result.voiceoverId);
        }

        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        dispatch({
          type: 'SINGLE_GENERATION_ERROR',
          payload: { sceneId, error: errorMessage, errorCode: 'INTERNAL_ERROR' },
        });
        toast.error('Voiceover regeneration failed', {
          description: errorMessage,
        });
        onSingleError?.(sceneId, errorMessage);
        return {
          success: false,
          error: errorMessage,
          errorCode: 'INTERNAL_ERROR',
        };
      }
    },
    [regenerateVoiceoverAction, onSingleSuccess, onSingleError]
  );

  /**
   * Generate voiceovers for all scenes in a reel
   */
  const handleGenerateBulk = useCallback(
    async (
      targetReelId: Id<'reels'>,
      voiceId?: Id<'clonedVoices'>
    ): Promise<IBulkVoiceoverResult> => {
      dispatch({ type: 'START_BULK_GENERATION', payload: { total: 0 } });

      try {
        const result = await generateBulkVoiceoversAction({
          reelId: targetReelId,
          voiceId,
        });

        // Update progress with final counts
        dispatch({
          type: 'BULK_PROGRESS',
          payload: {
            completed: result.successCount,
            failed: result.failedCount,
          },
        });

        dispatch({ type: 'BULK_GENERATION_COMPLETE', payload: { result } });

        if (result.success) {
          if (result.failedCount > 0) {
            // Get the first failure reason to show the user
            const firstFailure = result.results.find(
              (r: IBulkVoiceoverResult['results'][number]) => !r.success
            );
            const failureReason = firstFailure?.error || 'Unknown error';
            toast.warning('Voiceovers generated with some errors', {
              description: `${result.successCount} succeeded, ${result.failedCount} failed. First error: ${failureReason}`,
            });
          } else {
            toast.success('All voiceovers generated successfully!', {
              description: `${result.successCount} voiceovers created`,
            });
          }
        } else {
          // Get the first scene error for more detailed feedback
          const firstFailure = result.results.find(
            (r: IBulkVoiceoverResult['results'][number]) => !r.success
          );
          const errorMessage = firstFailure?.error || result.error || 'Unknown error';
          toast.error('Bulk generation failed', {
            description: errorMessage,
          });
        }

        onBulkComplete?.(result);
        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        dispatch({ type: 'BULK_GENERATION_ERROR', payload: { error: errorMessage } });
        toast.error('Bulk generation failed', {
          description: errorMessage,
        });
        onBulkError?.(errorMessage);
        return {
          success: false,
          totalScenes: 0,
          successCount: 0,
          failedCount: 0,
          skippedCount: 0,
          results: [],
          error: errorMessage,
        };
      }
    },
    [generateBulkVoiceoversAction, onBulkComplete, onBulkError]
  );

  /**
   * Get voiceover for a specific scene
   */
  const getVoiceoverForScene = useCallback(
    (sceneId: Id<'reelScenes'>): ISceneVoiceover | undefined => {
      return voiceoversMap.get(sceneId);
    },
    [voiceoversMap]
  );

  /**
   * Check if a scene is currently generating
   */
  const isSceneGenerating = useCallback(
    (sceneId: Id<'reelScenes'>): boolean => {
      return state.generatingSceneIds.includes(sceneId);
    },
    [state.generatingSceneIds]
  );

  /**
   * Check if a scene has a completed voiceover
   */
  const hasCompletedVoiceover = useCallback(
    (sceneId: Id<'reelScenes'>): boolean => {
      const voiceover = voiceoversMap.get(sceneId);
      return voiceover?.status === 'completed';
    },
    [voiceoversMap]
  );

  /**
   * Reset state
   */
  const handleReset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  // ============================================
  // RETURN API
  // ============================================

  return {
    // State
    state,
    step: state.step,
    isGenerating,
    isGeneratingSingle: state.isGeneratingSingle,
    isGeneratingBulk: state.isGeneratingBulk,
    generatingSceneId: state.generatingSceneId,
    generatingSceneIds: state.generatingSceneIds,
    error: state.error,
    errorCode: state.errorCode,

    // Bulk progress
    bulkProgress: state.bulkProgress,
    bulkProgressPercent,
    lastBulkResult: state.lastBulkResult,

    // Data
    voiceoversForReel: voiceoversForReel as ISceneVoiceover[] | undefined,
    voiceoversMap,
    completedVoiceovers,
    isLoading,

    // Actions
    generateSingle: handleGenerateSingle,
    regenerate: handleRegenerate,
    generateBulk: handleGenerateBulk,
    getVoiceoverForScene,
    isSceneGenerating,
    hasCompletedVoiceover,
    reset: handleReset,
  };
}
